import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { t } from "i18next";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import MDBox from "components/Basics/MDBox";

const RenderAssetDisplay = ({ document }) => {
	const [previewContent, setPreviewContent] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);

	const { url, contentType } = document;

	const toggleFullscreen = () => {
		setIsFullscreen(!isFullscreen);
	};

	const officeMimeTypes = [
		"application/msword",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		"application/vnd.ms-excel",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		"application/vnd.ms-powerpoint",
		"application/vnd.openxmlformats-officedocument.presentationml.presentation"
	];

	useEffect(() => {
		const generatePreview = async () => {
			// Réinitialiser previewContent au début pour éviter les résidus
			setPreviewContent(null);
			setIsLoading(true);

			try {
				switch (true) {
					// Images
					case contentType?.includes("image"): {
						setPreviewContent(
							<Box
								component="img"
								src={url}
								sx={{
									width: "100%",
									maxHeight: "300px",
									objectFit: "contain"
								}}
								alt="Asset Preview"
							/>
						);
						break;
					}
					// Videos
					case contentType?.includes("video"): {
						setPreviewContent(
							<Box
								component="video"
								src={url}
								controls
								sx={{
									width: "100%",
									maxHeight: "300px",
									objectFit: "contain"
								}}
							></Box>
						);
						break;
					}
					// html
					case contentType?.includes("text/html"): {
						setPreviewContent(
							<Box
								component="iframe"
								src={url}
								title={url || "HTML Preview"}
								sx={{ width: "100%", height: "100%", border: "none", objectFit: "contain" }}
							/>
						);
						break;
					}
					// pdf
					case contentType?.includes("application/pdf"): {
						setPreviewContent(
							<Box
								component="iframe"
								src={url}
								title="PDF Preview"
								sx={{
									width: "100%",
									height: "100%",
									border: "none",
									objectFit: "contain",
									minHeight: "300px"
								}}
							/>
						);
						break;
					}
					// office
					case officeMimeTypes.includes(contentType): {
						setPreviewContent(
							<Box
								component="iframe"
								src={`https://view.officeapps.live.com/op/embed.aspx?src="${encodeURIComponent(url)}"`}
								title="Office Document"
								sx={{
									width: "100%",
									height: "100%",
									border: "none",
									objectFit: "contain",
									minHeight: "300px"
								}}
							/>
						);
						break;
					}
					// default
					default: {
						// Cas par défaut si aucun type ne correspond
						setPreviewContent(
							<Typography variant="body2">
								{t("ACTIONS.ANALYSE_DOCUMENT_DISPLAY.no_preview_available")}
							</Typography>
						);
						break;
					}
				}
			} catch (error) {
				setPreviewContent(
					<Typography variant="body2">
						{t("ACTIONS.ANALYSE_DOCUMENT_DISPLAY.preview_error")}
					</Typography>
				);
			} finally {
				setIsLoading(false);
			}
		};

		generatePreview();
	}, [document]);

	return (
		<Box
			sx={{
				position: "relative",
				width: "100%",
				height: isFullscreen ? "100vh" : "100%",
				// border: "1px solid",
				// borderColor: "grey.300",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				// borderRadius: 1,
				backgroundColor: "white",
				color: "text.secondary",
				overflow: contentType?.includes("text/html") ? "auto" : "hidden",
				...(isFullscreen && {
					position: "fixed",
					top: 0,
					left: 0,
					zIndex: 9999,
					width: "100vw",
					backgroundColor: "#fff",
					// border: "none",
					// padding: 2,
					overflow: "auto"
				})
			}}
		>
			{!isLoading && (
				<Box sx={{ position: "absolute", bottom: 8, right: 8, display: "flex" }}>
					{/* Bouton Fullscreen */}
					<Tooltip title={isFullscreen ? t("SETTINGS.fullscreenExit") : t("SETTINGS.fullscreen")}>
						<IconButton
							onClick={toggleFullscreen}
							sx={{
								background: "rgba(255, 255, 255, 0.7)",
								"&:hover": {
									background: "#fff"
								}
							}}
						>
							{isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
						</IconButton>
					</Tooltip>
				</Box>
			)}

			{isLoading ? <CircularProgress size={40} /> : previewContent}
		</Box>
	);
};

export default RenderAssetDisplay;
